import {
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  MoveStageModal,
  PendingReviewBanner,
  SkipStageModal,
  StageHistory,
  StageProgressRail,
  SubmissionDecisionBanners,
} from "../components/SubmissionPipeline";
import { RaiseDisputeModal } from "../components/RaiseDisputeModal";
import { ResumeLink } from "../components/ResumeLink";
import { StatusBadge } from "../components/StatusBadge";
import { LoadingOverlay } from "../components/ui";
import { jobsApi, earningsApi, submissionsApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import {
  formatDate,
  formatExpectedPayoutWindow,
  formatPaise,
  formatCities,
  PAYOUT_STATUS_LABELS,
  payoutStatusColor,
  YOUR_PITCH_LABEL,
} from "../lib/format";
import { axioTokens } from "../theme";
import type { Stage } from "../types/api";

export function SubmissionDetailPage({
  basePath = "/submissions",
  jobsBasePath = "/jobs",
  readOnly = false,
}: {
  basePath?: string;
  jobsBasePath?: string;
  readOnly?: boolean;
}) {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [moveTarget, setMoveTarget] = useState<Stage | null>(null);
  const [skipTarget, setSkipTarget] = useState<Stage | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);

  const submissionQuery = useQuery({
    queryKey: ["submission", submissionId],
    queryFn: () => submissionsApi.get(submissionId!),
    enabled: !!submissionId,
  });

  const jobQuery = useQuery({
    queryKey: ["job", submissionQuery.data?.job_id],
    queryFn: () => jobsApi.get(submissionQuery.data!.job_id),
    enabled: !!submissionQuery.data?.job_id,
  });

  const payoutQuery = useQuery({
    queryKey: ["submission-payout", submissionId],
    queryFn: () => earningsApi.payouts({ submission_id: submissionId! }),
    enabled: !!submissionId && submissionQuery.data?.status === "hired" && !readOnly,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["submission", submissionId] });
    queryClient.invalidateQueries({ queryKey: ["my-submissions"] });
  };

  const moveMutation = useMutation({
    mutationFn: ({
      stageId,
      notes,
      markAs,
    }: {
      stageId: string;
      notes?: string;
      markAs?: "passed" | "skipped";
    }) =>
      submissionsApi.moveStage(submissionId!, {
        stage_id: stageId,
        notes,
        mark_as: markAs ?? "passed",
      }),
    onSuccess: (_data, variables) => {
      const skipped = variables.markAs === "skipped";
      notifications.show({
        color: "green",
        title: skipped ? "Stage skipped" : "Stage updated",
        message: skipped ? "Stage skipped and candidate advanced." : "Candidate moved to new stage.",
      });
      setMoveTarget(null);
      setSkipTarget(null);
      invalidate();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  const reviewMutation = useMutation({
    mutationFn: (notes?: string) => submissionsApi.submitForReview(submissionId!, { notes }),
    onSuccess: () => {
      notifications.show({
        color: "green",
        title: "Submitted for review",
        message: "HR will confirm the hire.",
      });
      setReviewModalOpen(false);
      setReviewNotes("");
      invalidate();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: () => submissionsApi.withdraw(submissionId!),
    onSuccess: () => {
      notifications.show({ color: "gray", title: "Withdrawn", message: "Submission withdrawn." });
      setWithdrawModalOpen(false);
      invalidate();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  if (submissionQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-300)" />;
  }

  const submission = submissionQuery.data;
  if (!submission) return <Text c="dimmed">Submission not found.</Text>;

  const stages = submission.stages;
  const lastStage = stages.length > 0 ? stages[stages.length - 1] : null;
  const onLastStage = lastStage && submission.current_stage?.id === lastStage.id;
  const isInProgress = submission.status === "in_progress";
  const canDispute = submission.status !== "withdrawn";
  const payout = payoutQuery.data?.items[0];

  return (
    <Stack gap={axioTokens.unit[32]}>
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>{submission.candidate.full_name}</Title>
          <Text c="dimmed">
            Applying for{" "}
            {jobQuery.data ? (
              <Anchor component={Link} to={`${jobsBasePath}/${submission.job_id}`} fw="var(--axio-font-weight-semibold)" c={axioTokens.ink}>
                {jobQuery.data.title}
              </Anchor>
            ) : (
              "…"
            )}
          </Text>
        </div>
        <StatusBadge status={submission.status} />
      </Group>

      <Button variant="subtle" color="gray" component={Link} to={basePath} w="fit-content" px="var(--axio-unit-0)">
        ← Back to submissions
      </Button>

      {submission.status === "pending_hr_review" && <PendingReviewBanner />}

      <SubmissionDecisionBanners
        status={submission.status}
        rejectionReason={submission.rejection_reason}
        history={submission.history}
      />

      {submission.status === "hired" && payout && (
        <Paper withBorder p="md">
          <Group justify="space-between" mb="sm">
            <Title order={5}>Placement Payout</Title>
            <Badge color={payoutStatusColor(payout.status)} variant="light">
              {PAYOUT_STATUS_LABELS[payout.status] ?? payout.status}
            </Badge>
          </Group>
          <Group gap="var(--axio-unit-40)">
            <div>
              <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
                Net payout
              </Text>
              <Text size="lg" className="moneyAmount">
                {formatPaise(payout.net_paise)}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
                {payout.status === "paid" ? "Paid on" : "Expected by"}
              </Text>
              <Text className="tabular">
                {payout.status === "paid" ? (
                  formatDate(payout.paid_at)
                ) : (
                  <>
                    {formatExpectedPayoutWindow(payout.placement.guarantee_until)}
                    <Text size="xs" c="dimmed">
                      10–30 days after guarantee
                    </Text>
                  </>
                )}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
                Guarantee until
              </Text>
              <Text className="tabular">{formatDate(payout.placement.guarantee_until)}</Text>
            </div>
          </Group>
        </Paper>
      )}

      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Hiring Flow Progress
        </Title>
        <StageProgressRail
          stages={stages}
          currentStageId={submission.current_stage?.id ?? null}
          status={submission.status}
          history={submission.history}
          interactive={isInProgress && !readOnly}
          loading={moveMutation.isPending}
          onAdvance={(nextStage) => setMoveTarget(nextStage)}
          onSkip={(nextStage) => setSkipTarget(nextStage)}
          onMoveToStage={(stage) => setMoveTarget(stage)}
        />
        {isInProgress && onLastStage && !readOnly && (
          <Button color="violet" mt="md" onClick={() => setReviewModalOpen(true)}>
            Submit for HR review
          </Button>
        )}
        {isInProgress && !readOnly && (
          <Button variant="subtle" color="red" size="xs" mt="md" onClick={() => setWithdrawModalOpen(true)}>
            Withdraw submission
          </Button>
        )}
        {canDispute && !readOnly && (
          <Button variant="outline" color="orange" size="xs" mt="md" ml="sm" onClick={() => setDisputeOpen(true)}>
            Raise a dispute
          </Button>
        )}
      </Paper>

      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Candidate Details
        </Title>
        <Group gap="var(--axio-unit-40)">
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              Phone
            </Text>
            <Text className="tabular">{submission.candidate.phone}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              City
            </Text>
            <Text>{formatCities(submission.candidate.cities)}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)">
              Resume
            </Text>
            <ResumeLink url={submission.candidate.resume_url} variant="link" />
          </div>
        </Group>
        {submission.pitch_note && (
          <>
            <Text size="xs" c="dimmed" fw="var(--axio-font-weight-semibold)" mt="md">
              {YOUR_PITCH_LABEL}
            </Text>
            <Text>{submission.pitch_note}</Text>
          </>
        )}
      </Paper>

      <StageHistory history={submission.history} stages={stages} />

      <MoveStageModal
        stage={moveTarget}
        opened={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        onConfirm={(notes) =>
          moveTarget &&
          moveMutation.mutate({
            stageId: moveTarget.id,
            notes: notes || undefined,
            markAs: "passed",
          })
        }
        loading={moveMutation.isPending}
        currentStageId={submission.current_stage?.id ?? null}
        stages={stages}
      />

      <SkipStageModal
        stage={skipTarget}
        opened={!!skipTarget}
        onClose={() => setSkipTarget(null)}
        onConfirm={(reason) =>
          skipTarget &&
          moveMutation.mutate({
            stageId: skipTarget.id,
            notes: reason,
            markAs: "skipped",
          })
        }
        loading={moveMutation.isPending}
      />

      <Modal
        opened={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        title="Submit for HR review"
        centered
      >
        <Stack>
          <Text size="sm" c="dimmed">
            HR will review and confirm the hire. You won't be able to edit stages after this.
          </Text>
          <Textarea
            label="Notes for HR (optional)"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.currentTarget.value)}
          />
          <Button
            color="violet"
            loading={reviewMutation.isPending}
            onClick={() => reviewMutation.mutate(reviewNotes || undefined)}
          >
            Submit for HR review
          </Button>
        </Stack>
      </Modal>

      <Modal opened={withdrawModalOpen} onClose={() => setWithdrawModalOpen(false)} title="Withdraw submission" centered>
        <Stack>
          <Text size="sm">Are you sure you want to withdraw this candidate from the job?</Text>
          <Button color="red" loading={withdrawMutation.isPending} onClick={() => withdrawMutation.mutate()}>
            Withdraw
          </Button>
        </Stack>
      </Modal>

      <RaiseDisputeModal
        opened={disputeOpen}
        onClose={() => setDisputeOpen(false)}
        submissionId={submission.id}
        defaultType={submission.status === "rejected" ? "quality" : "other"}
        onCreated={(id) => navigate(`/disputes/${id}`)}
      />
    </Stack>
  );
}
